import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Truck, ShoppingBag, Tag, AlertCircle, Search, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { trackEvent } from '@/components/Analytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DELIVERY_OPTIONS = [
  { id: "royal_mail_48", name: "Standard Delivery (Royal Mail 48)", timeline: "2-3 working days" },
];

export default function Checkout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { productId, quantity, product, couponCode } = location.state || {};

  const [deliveryOption, setDeliveryOption] = useState('royal_mail_48');
  const [billingSame, setBillingSame] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [pricing, setPricing] = useState(null);
  const [pricingLoading, setPricingLoading] = useState(true);
  // UK VAT-registered business (Style B invoice)
  const [isVatBusiness, setIsVatBusiness] = useState(false);
  const [vatNumber, setVatNumber] = useState('');
  const [businessName, setBusinessName] = useState('');

  const [form, setForm] = useState({
    firstName: '', lastName: '', email: '', phone: '',
    addressLine1: '', addressLine2: '', city: '', county: '', postcode: '',
    billingFirstName: '', billingLastName: '',
    billingAddressLine1: '', billingAddressLine2: '', billingCity: '', billingCounty: '', billingPostcode: '',
    orderNotes: '',
  });

  // UK postcode lookup state (must be declared before any early return - Rules of Hooks)
  const [postcodeLoading, setPostcodeLoading] = useState(false);
  const [postcodeError, setPostcodeError] = useState('');

  const isWholesale = quantity > 300;

  // IIY Rewards: fetch the signed-in customer's points balance (silent if guest).
  const [points, setPoints] = useState(null);
  const [redeem, setRedeem] = useState(false);
  useEffect(() => {
    axios.get(`${API}/customers/points`, { withCredentials: true })
      .then(r => setPoints(r.data))
      .catch(() => setPoints(null));
  }, []);

  // Fetch authoritative pricing from backend (applies any coupon, e.g. EMPYEE50 / MAY15)
  useEffect(() => {
    if (!quantity || isWholesale) { setPricingLoading(false); return; }
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.post(`${API}/calculate-price`, { quantity, coupon_code: couponCode || '', product_id: productId });
        if (!cancelled) setPricing(r.data);
      } catch (e) {
        if (!cancelled) toast.error('Failed to load pricing. Please reload.');
      } finally {
        if (!cancelled) setPricingLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [quantity, couponCode, isWholesale]);

  if (!productId || !quantity) {
    return (
      <div className="min-h-screen py-24 md:py-32 flex flex-col items-center justify-center">
        <p className="text-xl text-muted-foreground mb-4">No order in progress.</p>
        <button onClick={() => navigate('/products')} className="text-primary hover:underline">Browse Products</button>
      </div>
    );
  }

  // Derive display values from backend pricing (single source of truth)
  const subtotal = pricing ? Number(pricing.subtotal).toFixed(2) : null;
  const vat = pricing ? Number(pricing.vat_amount).toFixed(2) : null;
  const total = pricing ? Number(pricing.total).toFixed(2) : null;
  const pricePerBottle = pricing ? Number(pricing.price_per_bottle).toFixed(2) : null;
  const discountAmount = pricing ? Number(pricing.discount_amount).toFixed(2) : null;
  const discountPct = pricing ? pricing.discount_pct : 0;
  const couponApplied = pricing?.coupon_applied || null;

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  // ---- UK postcode lookup via postcodes.io (free, no API key required) ----
  // Looks up the supplied postcode and auto-populates city/county fields when
  // they're empty so the customer doesn't have to retype them. Lives entirely
  // client-side; we never send postcode lookups through our backend.
  const lookupPostcode = async (target = 'delivery') => {
    const raw = target === 'billing' ? form.billingPostcode : form.postcode;
    const code = (raw || '').trim().toUpperCase();
    if (!code) {
      setPostcodeError('Enter a postcode first.');
      return;
    }
    setPostcodeLoading(true);
    setPostcodeError('');
    try {
      // postcodes.io uses Access-Control-Allow-Origin: * which the browser
      // rejects when credentials mode is 'include'. axios.defaults has
      // withCredentials=true globally for our /api calls, so opt out per-call
      // for this third-party request.
      const r = await axios.get(`https://api.postcodes.io/postcodes/${encodeURIComponent(code)}`, { withCredentials: false });
      const result = r.data?.result;
      if (!result) throw new Error('Not found');
      const city = result.post_town || result.parish || result.admin_district || '';
      const county = result.admin_county || result.region || '';
      setForm((prev) => {
        if (target === 'billing') {
          return {
            ...prev,
            billingCity: prev.billingCity || city,
            billingCounty: prev.billingCounty || county,
            billingPostcode: result.postcode || code,
          };
        }
        return {
          ...prev,
          city: prev.city || city,
          county: prev.county || county,
          postcode: result.postcode || code,
        };
      });
      toast.success(`Address found: ${city}${county ? `, ${county}` : ''}`);
    } catch (err) {
      const status = err.response?.status;
      if (status === 404) setPostcodeError('Postcode not found. Please double-check it.');
      else setPostcodeError('Lookup failed. Please enter your address manually.');
    } finally {
      setPostcodeLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.firstName || !form.lastName || !form.email || !form.phone || !form.addressLine1 || !form.city || !form.postcode) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      const addressData = {
        first_name: form.firstName, last_name: form.lastName, email: form.email, phone: form.phone,
        address_line_1: form.addressLine1, address_line_2: form.addressLine2,
        city: form.city, county: form.county, postcode: form.postcode, country: "United Kingdom",
      };
      const billingData = billingSame ? null : {
        first_name: form.billingFirstName, last_name: form.billingLastName, email: form.email, phone: form.phone,
        address_line_1: form.billingAddressLine1, address_line_2: form.billingAddressLine2,
        city: form.billingCity, county: form.billingCounty, postcode: form.billingPostcode, country: "United Kingdom",
      };

      if (isWholesale) {
        // Pathway 2: Wholesale request (no payment)
        const response = await axios.post(`${API}/orders/wholesale`, {
          product_id: productId, quantity, delivery_option: deliveryOption,
          delivery_address: addressData, billing_same_as_delivery: billingSame,
          billing_address: billingData, order_notes: form.orderNotes,
        });
        navigate('/order-confirmation', { state: { order: response.data, isWholesale: true, quantity } });
      } else {
        // Pathway 1: Standard order with Stripe payment
        // GA4 conversion event — fires before Stripe redirect
        trackEvent('begin_checkout', {
          currency: 'GBP',
          value: Number(total || 0),
          items: [{
            item_id: productId,
            item_name: 'BCM-95®',
            price: Number(total || 0) / Math.max(1, quantity),
            quantity,
          }],
          coupon: couponCode || undefined,
          is_vat_business: !!isVatBusiness,
        });
        const response = await axios.post(`${API}/orders/checkout`, {
          product_id: productId, quantity, delivery_option: deliveryOption,
          delivery_address: addressData, billing_same_as_delivery: billingSame,
          billing_address: billingData, order_notes: form.orderNotes,
          origin_url: window.location.origin,
          coupon_code: couponCode || '',
          redeem_points: !!(redeem && points?.eligible_to_redeem),
          is_vat_business: !!isVatBusiness,
          vat_number: isVatBusiness ? (vatNumber || '').trim().toUpperCase() : '',
          business_name: isVatBusiness ? (businessName || '').trim() : '',
        });
        // Redirect to Stripe checkout
        window.location.href = response.data.checkout_url;
      }
      } catch (error) {
      // Show the specific reason (e.g. 422 missing field, 502 Stripe issue)
      // rather than a generic message so customer can self-correct and we get
      // a real diagnostic in the production console.
      // eslint-disable-next-line no-console
      console.error('Checkout failed:', error?.response?.status, error?.response?.data, error?.message);
      const detail = error?.response?.data?.detail;
      const msg = (typeof detail === 'string' && detail)
        || (Array.isArray(detail) && detail[0]?.msg)
        || error?.message
        || 'Failed to process order. Please try again or email sales@iiypv.com.';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen py-24 md:py-32 bg-gray-50">
      <div className="max-w-6xl mx-auto px-6 md:px-12">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-muted-foreground hover:text-primary mb-8 transition-colors" data-testid="checkout-back">
          <ArrowLeft className="w-5 h-5" /> Back
        </button>

        <h1 className="text-4xl font-bold mb-2 font-heading" data-testid="checkout-title">
          {isWholesale ? 'Wholesale Order Request' : 'Checkout'}
        </h1>
        {isWholesale && (
          <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl p-4 mb-8">
            <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-semibold text-amber-800">Custom Quote Required</p>
              <p className="text-sm text-amber-700">For orders of 301+ bottles, our sales team will review your request and provide a custom wholesale price (minimum £25/bottle all-inclusive of VAT). No payment is required now.</p>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-6">
              {/* Delivery Address */}
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-2xl p-6 shadow-sm">
                <h2 className="text-xl font-bold mb-4 font-heading">Delivery Address</h2>
                <div className="grid md:grid-cols-2 gap-4">
                  <div><label className="block text-sm font-medium mb-1">First Name *</label><input name="firstName" value={form.firstName} onChange={handleChange} required className="w-full px-4 py-3 rounded-xl border-2 border-border focus:border-primary focus:outline-none" data-testid="checkout-firstname" /></div>
                  <div><label className="block text-sm font-medium mb-1">Last Name *</label><input name="lastName" value={form.lastName} onChange={handleChange} required className="w-full px-4 py-3 rounded-xl border-2 border-border focus:border-primary focus:outline-none" data-testid="checkout-lastname" /></div>
                  <div><label className="block text-sm font-medium mb-1">Email Address *</label><input name="email" type="email" value={form.email} onChange={handleChange} required className="w-full px-4 py-3 rounded-xl border-2 border-border focus:border-primary focus:outline-none" data-testid="checkout-email" /></div>
                  <div><label className="block text-sm font-medium mb-1">Mobile Phone *</label><input name="phone" type="tel" value={form.phone} onChange={handleChange} required className="w-full px-4 py-3 rounded-xl border-2 border-border focus:border-primary focus:outline-none" placeholder="+44 7xxx xxxxxx" data-testid="checkout-phone" /></div>
                  <div className="md:col-span-2"><label className="block text-sm font-medium mb-1">Address Line 1 *</label><input name="addressLine1" value={form.addressLine1} onChange={handleChange} required className="w-full px-4 py-3 rounded-xl border-2 border-border focus:border-primary focus:outline-none" data-testid="checkout-address1" /></div>
                  <div className="md:col-span-2"><label className="block text-sm font-medium mb-1">Address Line 2</label><input name="addressLine2" value={form.addressLine2} onChange={handleChange} className="w-full px-4 py-3 rounded-xl border-2 border-border focus:border-primary focus:outline-none" /></div>
                  <div><label className="block text-sm font-medium mb-1">City *</label><input name="city" value={form.city} onChange={handleChange} required className="w-full px-4 py-3 rounded-xl border-2 border-border focus:border-primary focus:outline-none" data-testid="checkout-city" /></div>
                  <div><label className="block text-sm font-medium mb-1">County</label><input name="county" value={form.county} onChange={handleChange} className="w-full px-4 py-3 rounded-xl border-2 border-border focus:border-primary focus:outline-none" /></div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Postcode *</label>
                    <div className="flex gap-2">
                      <input
                        name="postcode"
                        value={form.postcode}
                        onChange={handleChange}
                        required
                        className="flex-1 px-4 py-3 rounded-xl border-2 border-border focus:border-primary focus:outline-none uppercase"
                        placeholder="NN1 2AB"
                        data-testid="checkout-postcode"
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); lookupPostcode('delivery'); } }}
                      />
                      <button
                        type="button"
                        onClick={() => lookupPostcode('delivery')}
                        disabled={postcodeLoading || !form.postcode}
                        className="px-4 py-3 rounded-xl bg-emerald-700 hover:bg-emerald-800 disabled:bg-gray-300 text-white text-sm font-medium flex items-center gap-1.5 transition-colors"
                        title="Auto-fill city and county from your UK postcode"
                        data-testid="checkout-postcode-lookup"
                      >
                        {postcodeLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                        <span className="hidden sm:inline">Find</span>
                      </button>
                    </div>
                    {postcodeError && (
                      <p className="text-xs text-red-600 mt-1" data-testid="checkout-postcode-error">{postcodeError}</p>
                    )}
                  </div>
                  <div><label className="block text-sm font-medium mb-1">Country</label><input value="United Kingdom" disabled className="w-full px-4 py-3 rounded-xl border-2 border-border bg-gray-50 text-muted-foreground" /></div>
                </div>
              </motion.div>

              {/* Billing */}
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-white rounded-2xl p-6 shadow-sm">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-bold font-heading">Billing Address</h2>
                  <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={billingSame} onChange={(e) => setBillingSame(e.target.checked)} className="w-4 h-4 text-primary rounded" data-testid="billing-same-checkbox" /><span className="text-sm">Same as delivery</span></label>
                </div>
                {!billingSame && (
                  <div className="grid md:grid-cols-2 gap-4">
                    <div><label className="block text-sm font-medium mb-1">First Name *</label><input name="billingFirstName" value={form.billingFirstName} onChange={handleChange} className="w-full px-4 py-3 rounded-xl border-2 border-border focus:border-primary focus:outline-none" /></div>
                    <div><label className="block text-sm font-medium mb-1">Last Name *</label><input name="billingLastName" value={form.billingLastName} onChange={handleChange} className="w-full px-4 py-3 rounded-xl border-2 border-border focus:border-primary focus:outline-none" /></div>
                    <div className="md:col-span-2"><label className="block text-sm font-medium mb-1">Address Line 1 *</label><input name="billingAddressLine1" value={form.billingAddressLine1} onChange={handleChange} className="w-full px-4 py-3 rounded-xl border-2 border-border focus:border-primary focus:outline-none" /></div>
                    <div><label className="block text-sm font-medium mb-1">City *</label><input name="billingCity" value={form.billingCity} onChange={handleChange} className="w-full px-4 py-3 rounded-xl border-2 border-border focus:border-primary focus:outline-none" /></div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Postcode *</label>
                      <div className="flex gap-2">
                        <input
                          name="billingPostcode"
                          value={form.billingPostcode}
                          onChange={handleChange}
                          className="flex-1 px-4 py-3 rounded-xl border-2 border-border focus:border-primary focus:outline-none uppercase"
                          data-testid="checkout-billing-postcode"
                          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); lookupPostcode('billing'); } }}
                        />
                        <button
                          type="button"
                          onClick={() => lookupPostcode('billing')}
                          disabled={postcodeLoading || !form.billingPostcode}
                          className="px-4 py-3 rounded-xl bg-emerald-700 hover:bg-emerald-800 disabled:bg-gray-300 text-white text-sm font-medium flex items-center gap-1.5 transition-colors"
                          title="Auto-fill billing city and county"
                          data-testid="checkout-billing-postcode-lookup"
                        >
                          {postcodeLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                          <span className="hidden sm:inline">Find</span>
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </motion.div>

              {/* Delivery Options */}
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="bg-white rounded-2xl p-6 shadow-sm">
                <h2 className="text-xl font-bold mb-4 font-heading flex items-center gap-2"><Truck className="w-5 h-5 text-primary" /> Delivery Options</h2>
                <div className="space-y-3">
                  {DELIVERY_OPTIONS.map((opt) => (
                    <label key={opt.id} className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all ${deliveryOption === opt.id ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'}`} data-testid={`delivery-${opt.id}`}>
                      <input type="radio" name="delivery" value={opt.id} checked={deliveryOption === opt.id} onChange={(e) => setDeliveryOption(e.target.value)} className="w-4 h-4 text-primary" />
                      <div className="flex-1"><p className="font-medium">{opt.name}</p><p className="text-sm text-muted-foreground">{opt.timeline}</p></div>
                      <span className="text-sm font-semibold text-green-600">FREE</span>
                    </label>
                  ))}
                </div>
              </motion.div>

              {/* Notes */}
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="bg-white rounded-2xl p-6 shadow-sm">
                <h2 className="text-xl font-bold mb-4 font-heading">Order Notes</h2>
                <textarea name="orderNotes" value={form.orderNotes} onChange={handleChange} rows={3} placeholder="Special delivery instructions or notes (optional)" className="w-full px-4 py-3 rounded-xl border-2 border-border focus:border-primary focus:outline-none resize-none" data-testid="checkout-notes" />
              </motion.div>

              {/* VAT-Registered Business toggle (Style B invoice) */}
              {!isWholesale && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.35 }}
                  className="bg-white rounded-2xl p-6 shadow-sm"
                  data-testid="vat-business-card"
                >
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={isVatBusiness}
                      onChange={(e) => setIsVatBusiness(e.target.checked)}
                      className="w-5 h-5 mt-0.5 text-primary rounded"
                      data-testid="vat-business-toggle"
                    />
                    <div>
                      <p className="font-semibold">I&apos;m purchasing for a UK VAT-registered business</p>
                      <p className="text-sm text-muted-foreground mt-0.5">
                        We&apos;ll issue an itemised VAT invoice (net + 20% VAT breakdown) so you can reclaim eligible VAT through HMRC.
                      </p>
                    </div>
                  </label>

                  {isVatBusiness && (
                    <div className="mt-4 grid md:grid-cols-2 gap-4 pl-8" data-testid="vat-business-fields">
                      <div>
                        <label className="block text-sm font-medium mb-1">Registered Business Name *</label>
                        <input
                          value={businessName}
                          onChange={(e) => setBusinessName(e.target.value)}
                          required={isVatBusiness}
                          placeholder="Acme Health Ltd"
                          className="w-full px-4 py-3 rounded-xl border-2 border-border focus:border-primary focus:outline-none"
                          data-testid="vat-business-name"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">UK VAT Number *</label>
                        <input
                          value={vatNumber}
                          onChange={(e) => setVatNumber(e.target.value.toUpperCase())}
                          required={isVatBusiness}
                          placeholder="GB123456789"
                          className="w-full px-4 py-3 rounded-xl border-2 border-border focus:border-primary focus:outline-none uppercase"
                          data-testid="vat-business-number"
                        />
                      </div>
                    </div>
                  )}
                </motion.div>
              )}
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="bg-white rounded-2xl p-6 shadow-sm sticky top-24">
                <h2 className="text-xl font-bold mb-4 font-heading">Order Summary</h2>
                <div className="flex items-center gap-4 mb-4 pb-4 border-b border-border">
                  {product && <img loading="lazy" decoding="async" src={product.image} alt={product.name} className="w-16 h-16 rounded-lg object-cover" />}
                  <div>
                    <p className="font-semibold text-sm">{product?.name || 'IIY BCM-95®'}</p>
                    <p className="text-xs text-muted-foreground">60 Capsules per Bottle</p>
                    <p className="text-sm font-medium mt-1">Qty: {quantity}</p>
                  </div>
                </div>

                {isWholesale ? (
                  <div className="text-center py-4">
                    <p className="text-lg font-bold text-amber-700">Custom Quote Required</p>
                    <p className="text-sm text-muted-foreground mt-2">Our team will provide pricing within 24 hours</p>
                    <p className="text-xs text-muted-foreground mt-1">Minimum: £25/bottle all-inclusive of VAT</p>
                  </div>
                ) : pricingLoading || !pricing ? (
                  <div className="py-8 flex items-center justify-center" data-testid="checkout-pricing-loading">
                    <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-primary"></div>
                  </div>
                ) : (
                  <>
                    <div className="space-y-2 text-sm mb-4">
                      <div className="flex justify-between" data-testid="checkout-line-subtotal"><span>{quantity} × £{pricePerBottle}</span><span>£{(Number(pricePerBottle) * quantity).toFixed(2)}</span></div>
                      {parseFloat(discountAmount) > 0 && !couponApplied && (
                        <div className="flex justify-between text-green-600"><span className="flex items-center gap-1"><Tag className="w-3 h-3" /> Volume discount ({discountPct}%)</span><span>-£{discountAmount}</span></div>
                      )}
                      {couponApplied && (
                        <div className="flex justify-between text-amber-700 font-medium" data-testid="checkout-coupon-line">
                          <span className="flex items-center gap-1">
                            <Tag className="w-3 h-3" />
                            {couponApplied.auto_applied
                              ? `Single Bottle Offer (${couponApplied.value_pct}% off · auto-applied)`
                              : `Code ${couponApplied.code}`}
                          </span>
                          <span>-£{Number(couponApplied.saved_amount).toFixed(2)}</span>
                        </div>
                      )}
                      {isVatBusiness && (
                        <div className="pt-2 mt-1 border-t border-dashed border-border space-y-1.5" data-testid="checkout-vat-breakdown">
                          <p className="text-xs uppercase tracking-wide text-muted-foreground font-semibold">VAT breakdown (for your records)</p>
                          <div className="flex justify-between text-muted-foreground"><span>Net (ex-VAT)</span><span>£{subtotal}</span></div>
                          <div className="flex justify-between text-muted-foreground"><span>VAT (20%)</span><span>£{vat}</span></div>
                        </div>
                      )}
                    </div>

                    {points?.eligible_to_redeem && (
                      <label className="flex items-start gap-2.5 mt-3 p-3 rounded-xl bg-emerald-50 border border-emerald-200 cursor-pointer" data-testid="checkout-redeem-toggle">
                        <input type="checkbox" checked={redeem} onChange={(e) => setRedeem(e.target.checked)} className="mt-0.5 w-4 h-4 accent-emerald-700" data-testid="checkout-redeem-checkbox" />
                        <span className="text-sm text-emerald-900">
                          <b>Apply £5 reward</b> — use 500 of your {points.balance} IIY Rewards points on this order.
                        </span>
                      </label>
                    )}
                    {redeem && points?.eligible_to_redeem && (
                      <div className="flex justify-between text-emerald-700 font-medium mt-2" data-testid="checkout-reward-line">
                        <span>IIY Rewards (−500 points)</span><span>-£5.00</span>
                      </div>
                    )}
                    <div className="flex justify-between font-bold text-xl pt-4 border-t border-border" data-testid="checkout-total">
                      <span>Total</span><span>£{(Number(total) - ((redeem && points?.eligible_to_redeem) ? 5 : 0)).toFixed(2)}</span>
                    </div>
                    {points && (
                      <p className="text-[11px] text-emerald-700 mt-2 leading-snug" data-testid="checkout-earn-note">
                        {points.eligible_to_redeem
                          ? `You'll earn ${Math.floor((Number(total) / 1.2) * points.earn_rate)} points on this order.`
                          : `You'll earn ${Math.floor((Number(total) / 1.2) * points.earn_rate)} points — you're ${points.points_to_next_reward} points from your next £5 reward.`}
                      </p>
                    )}
                    <p className="text-[11px] text-muted-foreground mt-2 leading-snug" data-testid="checkout-allin-note">
                      Price is all-inclusive of UK VAT (20%) and standard UK delivery (Royal Mail 48). No further charges at checkout.
                    </p>
                  </>
                )}

                <button type="submit" disabled={submitting || (!isWholesale && (pricingLoading || !pricing))} className="w-full mt-6 bg-primary hover:bg-primary/90 text-white font-semibold py-4 px-6 rounded-xl transition-all duration-300 hover:-translate-y-1 flex items-center justify-center gap-2 shadow-lg hover:shadow-xl disabled:opacity-50" data-testid="submit-order-button">
                  <ShoppingBag className="w-5 h-5" />
                  {submitting ? 'Processing...' : isWholesale ? 'Submit Wholesale Request' : `Pay Now — £${total != null ? (Number(total) - ((redeem && points?.eligible_to_redeem) ? 5 : 0)).toFixed(2) : '...'}`}
                </button>

                <p className="text-xs text-center text-muted-foreground mt-4">
                  {isWholesale ? 'No payment required. Our team will contact you with pricing.' : '100% payment required. Secure checkout via Stripe.'}
                </p>
              </motion.div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
